import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { CardModule } from 'primeng/card';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse, HttpClientModule } from '@angular/common/http';
import { MessageService, ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { TrackedDialogService } from '../../../core/services/tracked-dialog.service';
import { EmPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { Permission } from '../../../core/enums/permission.enum';
import { RoleAssignmentDialogComponent } from './role-assignment-dialog/role-assignment-dialog.component';
import { RolePermissionBridgeService } from '../../../core/services/role-permission-bridge.service';
import { PermissionService } from '../../../core/services/permission.service';
import { SharedRoleService } from '../../../core/services/shared-role.service';
import { Role } from '../../../core/models/role';
import { RoleService } from '../../../core/services/role.service';
import { UserRolesService } from '../../../core/services/user-roles.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToolbarModule } from 'primeng/toolbar';
import { ChangeDetectorRef } from '@angular/core';
import { OverlayPanelModule } from 'primeng/overlaypanel';
interface User {
  id: number;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  photo?: string;
  profileImage?: string;
  department?: string;
  modifiedDate?: Date | null;
  archived?: boolean;
  userId?: number;
  credentialsSent?: boolean;
}

interface UserRoleAssignment {
  id: number;
  user: User;
  role: Role;
  assignedDate: Date;
  assignedBy: string;
  modifiedDate?: Date | null;
}

interface UserRole {
  id: number;
  user: User;
  roles: Role[];
  assignedDate?: Date;
  assignedBy?: string;
}


@Component({
  selector: 'em-role-users',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    CheckboxModule,
    ButtonModule,
    ToastModule,
    TooltipModule,
    ConfirmDialogModule,
    TagModule,
    CardModule,
    DropdownModule,
    MultiSelectModule,
    TranslateModule,
    EmPermissionDirective,
    HttpClientModule,
    ToolbarModule,
    OverlayPanelModule
  ],
  providers: [MessageService, ConfirmationService, TrackedDialogService],
  templateUrl: './role-users.component.html',
  styleUrls: ['./role-users.component.scss']
})
export class RoleUsersComponent implements OnInit {
  @ViewChild('resetPasswordPanel') resetPasswordPanel!: any;
  userRoleAssignments: UserRoleAssignment[] = [];
  userRoles: UserRole[] = [];
  selectedUserRoles: UserRole[] = [];
  selectedUserRoleAssignments: UserRoleAssignment[] = [];
  userRoleForm: FormGroup;

  users: User[] = [];
  roles: Role[] = [];
  selectedRoles: Role[] = [];
  editingUserRole: UserRole | null = null;
  loading: boolean = false;
  readonly Permission = Permission;
  uniqueUsers: User[] = [];
  showFilterRow = false;
  filtersForm!: FormGroup;
  filteredUserRoles: User[] = [];
  totalRecords: number = 0;
  userToReset: User | null = null;
  constructor(
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private formBuilder: FormBuilder,
    private dialogService: DialogService,
    private rolePermissionBridge: RolePermissionBridgeService,
    private permissionService: PermissionService,
    private sharedRoleService: SharedRoleService,
    private roleService: RoleService,
    private userRolesService: UserRolesService,
    private authService: AuthService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {
    this.userRoleForm = this.formBuilder.group({
      selectedUser: ['', Validators.required],
      selectedRoles: [[], Validators.required]
    });
  }

  ngOnInit(): void {
    this.loading = true;
    this.loadRolesFromApi();
    this.loadUsersWithRolesFromApi();
    this.filtersForm = this.initFilterForm();
  }
  initFilterForm() {
    return new FormGroup({
      user: new FormControl(null),
      assignedRole: new FormControl(null),
    });
  }
  clearAllFilters(): void {
    this.filtersForm.reset()
    this.applyFilters();
  }
  toggleFilters(): void {
    this.showFilterRow = !this.showFilterRow;
    if (!this.showFilterRow) {
      this.filtersForm.reset()
      this.applyFilters();
    }
  }
  applyFilters(): void {

    if (!this.uniqueUsers || this.uniqueUsers.length === 0) {
      this.filteredUserRoles = [];
      this.totalRecords = 0;
      return;
    }

    const { user, assignedRole } = this.filtersForm.value;

    const userFilter = (user ?? '').toString().trim().toLowerCase();
    const assignedRoleFilters: string[] = assignedRole || [];

    let filtered = [...this.uniqueUsers];

    if (userFilter) {
      filtered = filtered.filter(u =>
        (`${u.firstName} ${u.lastName}`.toLowerCase().includes(userFilter))
      );
    }

    if (assignedRoleFilters.length > 0) {
      filtered = filtered.filter(u =>
        this.userRoleAssignments
          .filter(a => a.user.id === u.id)
          .some(a => assignedRoleFilters.includes(a.role.name))
      );
    }

    this.filteredUserRoles = [...filtered];
    this.totalRecords = this.filteredUserRoles.length;
  }

  archiveUser(user: User): void {
    const userId = this.getBackendUserId(user);
    if (userId == null) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.toast.error'),
        detail: this.translate.instant('layout.configurations.users.assignRolesToUser.archiveUser.missingId')
      });
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant(
        'layout.configurations.users.assignRolesToUser.archiveUser.confirm',
        { name: user.name }
      ),
      header: this.translate.instant(
        'layout.configurations.users.assignRolesToUser.archiveUser.title'
      ) || 'Confirm Archive',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translate.instant(
        'layout.configurations.users.assignRolesToUser.archiveUser.archive'
      ) || 'Archive',
      rejectLabel: this.translate.instant(
        'layout.configurations.users.assignRolesToUser.archiveUser.cancel'
      ) || 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.roleService.archiveUser(userId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant(
                'layout.configurations.users.assignRolesToUser.archiveUser.success.title'
              ) || 'Success',
              detail: this.translate.instant(
                'layout.configurations.users.assignRolesToUser.archiveUser.success.message',
                { name: user.name }
              )
            });
            this.loadUsersWithRolesFromApi(); // Refresh the list
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.toast.error'),
              detail: err.error?.message || this.translate.instant('layout.configurations.users.messages.archiveFailed')
            });
          }
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('layout.configurations.users.assignRolesToUser.archiveUser.cancelled.title'),
          detail: this.translate.instant('layout.configurations.users.assignRolesToUser.archiveUser.cancelled.message'),
          life: 3000
        });
      }
    });
  }

  reactivateUser(user: User): void {
    const userId = this.getBackendUserId(user);
    if (userId == null) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.toast.error'),
        detail: this.translate.instant('layout.configurations.users.assignRolesToUser.reactivateUser.missingId')
      });
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant(
        'layout.configurations.users.assignRolesToUser.reactivateUser.confirm',
        { name: user.name }
      ),
      header: this.translate.instant(
        'layout.configurations.users.assignRolesToUser.reactivateUser.title'
      ) || 'Confirm Reactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('layout.configurations.users.assignRolesToUser.reactivateUser.reactivate'),
      rejectLabel: this.translate.instant('layout.configurations.users.assignRolesToUser.reactivateUser.cancel'),
      acceptButtonStyleClass: 'p-button-success',
      accept: () => {
        this.roleService.reactivateUser(userId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('layout.configurations.users.assignRolesToUser.reactivateUser.success.title'),
              detail: this.translate.instant('layout.configurations.users.assignRolesToUser.reactivateUser.success.message', { name: user.name })
            });
            this.loadUsersWithRolesFromApi();
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.toast.error'),
              detail: err.error?.message || this.translate.instant('layout.configurations.users.messages.reactivateFailed')
            });
          }
        });
      }
    });
  }

  isCurrentUser(user: any): boolean {
    if (!user) return false;
    const currentUserIdStr = localStorage.getItem('userId');
    const currentEmployeeIdStr = localStorage.getItem('employeeId');
    const currentUserId = currentUserIdStr ? Number(currentUserIdStr) : null;
    const currentEmployeeId = currentEmployeeIdStr ? Number(currentEmployeeIdStr) : null;

    if (user.userId != null && currentUserId != null && Number(user.userId) === currentUserId) {
      return true;
    }
    if (user.id != null && currentEmployeeId != null && Number(user.id) === currentEmployeeId) {
      return true;
    }
    return false;
  }

  shareCredentials(user: any): void {
    if (this.isCurrentUser(user)) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.warning.title') || 'Warning',
        detail: this.translate.instant('layout.configurations.users.shareCredentialsOwnAccount')
      });
      return;
    }

    const userId = this.getBackendUserId(user) || user?.id;
    if (!userId) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.toast.error') || 'Error',
        detail: this.translate.instant('layout.configurations.users.shareCredentialsError') || 'Cannot share credentials: User ID is missing.'
      });
      return;
    }

    const userName = (user.firstName && user.lastName) ? `${user.firstName} ${user.lastName}` : (user.name || user.email || 'User');

    this.confirmationService.confirm({
      message: this.translate.instant(
        'layout.configurations.users.shareCredentialsConfirm',
        { name: userName }
      ),
      header: this.translate.instant(
        'layout.configurations.users.shareCredentials'
      ) || 'Share Credentials',
      icon: 'pi pi-envelope',
      acceptLabel: this.translate.instant('layout.configurations.users.share'),
      rejectLabel: this.translate.instant('common.cancel'),
      acceptButtonStyleClass: 'p-button-help',
      accept: () => {
        this.userRolesService.shareCredentials(userId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('common.toast.success') || 'Success',
              detail: this.translate.instant(
                'layout.configurations.users.shareCredentialsSuccess',
                { name: userName }
              )
            });
            this.loadUsersWithRolesFromApi();
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.toast.error') || 'Error',
              detail: err.error?.message || this.translate.instant('layout.configurations.users.shareCredentialsFailed') || 'Failed to share credentials'
            });
          }
        });
      }
    });
  }

  shareCredentialsToNewUsers(): void {
    const unnotifiedUsers = this.filteredUserRoles.filter(u => !u.archived && !u.credentialsSent && !this.isCurrentUser(u));
    const count = unnotifiedUsers.length;

    this.confirmationService.confirm({
      message: this.translate.instant(
        'layout.configurations.users.sendToNewUsersConfirm',
        { count: count }
      ),
      header: this.translate.instant('layout.configurations.users.sendToNewUsers') || 'Send to New Users',
      icon: 'pi pi-send',
      acceptLabel: this.translate.instant('layout.configurations.users.send'),
      rejectLabel: this.translate.instant('common.cancel'),
      acceptButtonStyleClass: 'p-button-info',
      accept: () => {
        this.userRolesService.shareCredentialsToNewUsers().subscribe({
          next: (res) => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('common.toast.success') || 'Success',
              detail: this.translate.instant(
                'layout.configurations.users.sendToNewUsersSuccess',
                { count: res?.processedCount ?? count }
              )
            });
            this.loadUsersWithRolesFromApi();
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.toast.error') || 'Error',
              detail: err.error?.message || this.translate.instant('layout.configurations.users.sendToNewUsersFailed') || 'Failed to send credentials to new users'
            });
          }
        });
      }
    });
  }

  getAvatarBg(user: any): string {
    const seed = (user?.firstName || '') + (user?.lastName || '');
    const colors = ['#248BDF', '#4BA24E', '#F2B92C', '#D488A2', '#115081', '#989898'];
    let sum = 0; for (let i = 0; i < seed.length; i++) sum += seed.charCodeAt(i);
    return colors[sum % colors.length];
  }

  private loadRolesFromApi(): void {
    this.roleService.getAllRoles().subscribe({
      next: (roles) => {
        this.roles = (roles || []).map(r => ({ id: r.id, name: r.name, description: r.description })) as Role[];
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.add({ severity: 'error', summary: this.translate.instant('common.toast.error'), detail: this.translate.instant('layout.configurations.users.messages.loadRolesFailed') });
      }
    });
  }

  private loadUsersWithRolesFromApi(search?: string): void {
    this.userRolesService.getUsersWithRoles(true, search).subscribe({
      next: (users) => {
        this.users = (users || []).map(u => ({
          id: u.id,
          firstName: u.firstName,
          lastName: u.lastName,
          name: `${u.firstName} ${u.lastName}`,
          email: u.email || '',
          profileImage: u.profileImage,
          department: u.department,
          modifiedDate: u.modifiedDate,
          archived: u.archived || false,
          userId: u.userId,
          credentialsSent: u.credentialsSent || false
        }));

        this.userRoleAssignments = (users || []).flatMap(u =>
          (u.roles || []).map((r, idx) => ({
            id: Number(`${u.id}${idx}`),
            user: this.users.find(x => x.id === u.id)!,
            role: { id: r.role.id, name: r.role.name, description: r.role.description } as Role,
            assignedDate: r.assignedDate ? new Date(r.assignedDate) : new Date(),
            assignedBy: r.assignedBy || ''
          }))
        );

        this.generateGroupedUserRoles();
        const uniqueUserMap = new Map<number, User>();
        this.userRoleAssignments.forEach(a => uniqueUserMap.set(a.user.id, a.user));

        this.uniqueUsers = Array.from(uniqueUserMap.values()).sort((userA, userB) => {
          const assignmentsA = this.userRoleAssignments.filter(a => a.user.id === userA.id);
          const assignmentsB = this.userRoleAssignments.filter(a => a.user.id === userB.id);

          const maxDateA = assignmentsA.length > 0
            ? Math.max(...assignmentsA.map(a => a.assignedDate ? new Date(a.assignedDate).getTime() : -Infinity))
            : -Infinity;
          const maxDateB = assignmentsB.length > 0
            ? Math.max(...assignmentsB.map(a => a.assignedDate ? new Date(a.assignedDate).getTime() : -Infinity))
            : -Infinity;

          if (maxDateA !== maxDateB) return maxDateB - maxDateA;
          return userB.id - userA.id;
        });

        this.filteredUserRoles = [...this.uniqueUsers];
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.messageService.add({ severity: 'error', summary: this.translate.instant('common.toast.error'), detail: this.translate.instant('layout.configurations.users.messages.loadUsersFailed') });
        this.loading = false;
      }
    });
  }

  initializeUserRoleAssignments(): void {
    const findRole = (roleName: string) => {
      const role = this.roles.find(r => r.name === roleName);
      if (role && !role.id) {
        role.id = this.roles.indexOf(role) + 1;
      }
      return role;
    };

    this.userRoleAssignments = [
      {
        id: 1,
        user: this.users[0],
        role: findRole('Super Admin')!,
        assignedDate: new Date('2024-01-15'),
        assignedBy: 'System Admin'
      },
      {
        id: 2,
        user: this.users[0],
        role: findRole('Process Technician 1')!,
        assignedDate: new Date('2024-01-20'),
        assignedBy: 'System Admin'
      },
      {
        id: 3,
        user: this.users[1],
        role: findRole('CSSL')!,
        assignedDate: new Date('2024-02-01'),
        assignedBy: 'Department Head'
      },
      {
        id: 4,
        user: this.users[1],
        role: findRole('Pool Admin')!,
        assignedDate: new Date('2024-02-05'),
        assignedBy: 'Department Head'
      },
      {
        id: 5,
        user: this.users[2],
        role: findRole('CEO')!,
        assignedDate: new Date('2024-02-10'),
        assignedBy: 'Team Lead'
      },
      {
        id: 6,
        user: this.users[3],
        role: findRole('Process Team Leader')!, // i18n-ignore (role name lookup)
        assignedDate: new Date('2024-02-15'),
        assignedBy: 'System Admin'
      },
      {
        id: 7,
        user: this.users[3],
        role: findRole('Evaluation Admin')!,
        assignedDate: new Date('2024-02-20'),
        assignedBy: 'System Admin'
      },
      {
        id: 8,
        user: this.users[4],
        role: findRole('Promotion Admin')!,
        assignedDate: new Date('2024-03-01'),
        assignedBy: 'HR Director'
      },
      {
        id: 9,
        user: this.users[5],
        role: findRole('Pool')!,
        assignedDate: new Date('2024-03-05'),
        assignedBy: 'Pool Manager'
      },
      {
        id: 10,
        user: this.users[6],
        role: findRole('Promotion')!,
        assignedDate: new Date('2024-03-10'),
        assignedBy: 'System Admin'
      },
      {
        id: 11,
        user: this.users[6],
        role: findRole('Evaluation')!,
        assignedDate: new Date('2024-03-12'),
        assignedBy: 'System Admin'
      }
    ];

    this.generateGroupedUserRoles();
  }

  generateGroupedUserRoles(): void {
    const userRoleMap = new Map<number, UserRole>();

    this.userRoleAssignments.forEach(assignment => {
      const userId = assignment.user.id;

      if (!userRoleMap.has(userId)) {
        userRoleMap.set(userId, {
          id: userId,
          user: assignment.user,
          roles: [],
          assignedDate: assignment.assignedDate,
          assignedBy: assignment.assignedBy
        });
      }

      const userRole = userRoleMap.get(userId)!;
      userRole.roles.push(assignment.role);

      if (userRole.assignedDate && assignment.assignedDate < userRole.assignedDate) {
        userRole.assignedDate = assignment.assignedDate;
        userRole.assignedBy = assignment.assignedBy;
      }
    });

    this.userRoles = Array.from(userRoleMap.values());
  }

  getGroupedUserRoleAssignments(): { [userId: number]: UserRoleAssignment[] } {
    const grouped: { [userId: number]: UserRoleAssignment[] } = {};

    this.userRoleAssignments.forEach(assignment => {
      const userId = assignment.user.id;
      if (!grouped[userId]) {
        grouped[userId] = [];
      }
      grouped[userId].push(assignment);
    });

    return grouped;
  }

  getUniqueUsers(): User[] {
    const userMap = new Map<number, User>();
    this.userRoleAssignments.forEach(assignment => {
      userMap.set(assignment.user.id, assignment.user);
    });
    return Array.from(userMap.values());
  }

  getInitials(name: string): string {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  }
  private makeInitialsDataUrl(first?: string, last?: string): string {
    const f = (first || '').trim()[0] || '';
    const lastParts = (last || '').trim().split(/\s+/).filter(Boolean);
    const l = lastParts.length ? lastParts[lastParts.length - 1][0] : '';
    const initials = ((f + l) || 'U').toUpperCase();

    const bg = '#248BDF';
    const svg = `
    <svg xmlns='http://www.w3.org/2000/svg' width='64' height='64'>
      <rect width='100%' height='100%' rx='999' ry='999' fill='${bg}'/>
      <text x='50%' y='54%' dominant-baseline='middle' text-anchor='middle'
            font-family='Arial' font-size='26' fill='white'>${initials}</text>
    </svg>`;
    return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
  }


  getPlaceholderImage(user: any): string {
    return this.makeInitialsDataUrl(user?.firstName, user?.lastName);
  }

  onImageError(evt: Event, user: any) {
    const img = evt.target as HTMLImageElement;
    img.onerror = null;
    img.src = this.getPlaceholderImage(user);
  }

  getRoleSeverity(roleName: string): string {
    const roleUpper = roleName.toUpperCase();
    switch (roleUpper) {
      case 'SUPER_ADMIN':
        return 'danger';
      case 'MANAGER_CEO':
      case 'CSSL':
        return 'warning';
      case 'PROCESS_TECHNICIAN_1':
      case 'PROCESS_TECHNICIAN_2':
      case 'PROCESS_TECHNICIAN_3':
      case 'PROCESS_TECHNICIAN_4':
      case 'PROCESS_TEAM_LEADER':
        return 'info';
      case 'CSL':
      case 'POOL_ADMIN':
      case 'EVALUATION_ADMIN':
      case 'PROMOTION_ADMIN':
        return 'contrast';
      case 'EMPLOYEE':
      case 'POOL':
      case 'PROMOTION':
      case 'EVALUATION':
        return 'success';
      default:
        return 'secondary';
    }
  }

  removeRoleAssignment(assignment: UserRoleAssignment): void {
    if (!assignment || !assignment.id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.toast.error'),
        detail: this.translate.instant('layout.configurations.users.messages.invalidRoleAssignment')
      });
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('layout.configurations.users.messages.confirmRemoveRole', { role: assignment.role.name, name: assignment.user.name }),
      header: this.translate.instant('layout.configurations.users.messages.confirmRoleRemovalTitle'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {

        console.log('DELETE REQUEST:', {
          userId: assignment.user?.id,
          roleId: assignment.role?.id,
          userName: assignment.user?.name,
          roleName: assignment.role?.name,
          fullAssignment: assignment
        });

        if (!assignment.role?.id) {
          this.messageService.add({ severity: 'error', summary: this.translate.instant('common.toast.error'), detail: this.translate.instant('layout.configurations.users.messages.invalidRoleId', { role: JSON.stringify(assignment.role) }) });
          return;
        }

        this.userRolesService.removeUserRole(assignment.user.id, assignment.role.id).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('common.toast.success'),
              detail: this.translate.instant('layout.configurations.users.messages.roleRemoved', { role: assignment.role.name, name: assignment.user.name })
            });

            setTimeout(() => {
              this.loadUsersWithRolesFromApi();
              this.cdr.detectChanges();
            }, 100);
          },
          error: (err: HttpErrorResponse) => {
            console.error('DELETE FAILED:', err);
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.toast.error'),
              detail: this.translate.instant('layout.configurations.users.messages.failedWithReason', { reason: err.error?.message || err.message })
            });
          }
        });
      }
    });
  }

  viewUserRoles(user: User): void {
    const userAssignments = this.userRoleAssignments.filter(assignment => assignment.user.id === user.id);

    if (userAssignments.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: this.translate.instant('layout.configurations.users.messages.noRoles'),
        detail: this.translate.instant('layout.configurations.users.messages.noRoleAssignments', { name: user.name })
      });
      return;
    }

    const userRole: UserRole = {
      id: user.id,
      user: user,
      roles: userAssignments.map(assignment => assignment.role),
      assignedDate: userAssignments[0].assignedDate,
      assignedBy: userAssignments[0].assignedBy
    };

    //  Filter to only active users
    const activeUsers = this.users.filter(u => !u.archived);

    const ref = this.dialogService.open(RoleAssignmentDialogComponent, {
      header: this.translate.instant('layout.configurations.users.messages.viewRolesFor', { name: user.name }),
      width: '750px',
      modal: true,
      closable: true,
      maximizable: false,
      data: {
        users: activeUsers,  //  Only active users
        roles: this.roles,
        isEditMode: true,
        existingUserRole: userRole,
        viewOnly: true
      }
    });
  }

  assignRolesToUser(user: User): void {
    const ref = this.dialogService.open(RoleAssignmentDialogComponent, {
      header: this.translate.instant('layout.configurations.users.assignRolesToUser.assignRolesTo', { name: user.name }),
      width: '750px',
      modal: true,
      closable: true,
      maximizable: false,
      data: {
        users: [user],
        roles: this.roles,
        isEditMode: false,
        preSelectedUser: user,
      },
    });

    ref.onClose.subscribe((result) => {
      if (result && result.success) {
        this.loadUsersWithRolesFromApi();
      }
    });
  }

  editRoleAssignment(assignment: UserRoleAssignment): void {

    if (assignment.user.archived) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.toast.error') || 'Error',
        detail: this.translate.instant('layout.configurations.users.assignRolesToUser.editArchivedUserError')
      });
      return;
    }

    const ref = this.dialogService.open(RoleAssignmentDialogComponent, {
      header: this.translate.instant('layout.configurations.users.messages.editRoleAssignmentFor', { name: assignment.user.name }),
      width: '750px',
      modal: true,
      closable: true,
      maximizable: false,
      data: {
        users: [assignment.user],
        roles: this.roles,
        isEditMode: true,
        editingSingleRole: true,
        existingAssignment: assignment,
        preSelectedUser: assignment.user,
        preSelectedRole: assignment.role
      }
    });

    ref.onClose.subscribe((result) => {
      if (result && result.action === 'edit' && result.data.role) {
        if (result.data.role.id === assignment.role.id) {
          this.messageService.add({
            severity: 'info',
            summary: this.translate.instant('layout.configurations.users.messages.noChanges.title'),
            detail: this.translate.instant('layout.configurations.users.messages.noChanges.detail')
          });
          return;
        }

        const existingIndex = this.userRoleAssignments.findIndex(a => a.id === assignment.id);
        if (existingIndex !== -1) {
          const isDuplicate = this.userRoleAssignments.some(a =>
            a.user.id === assignment.user.id &&
            a.role.id === result.data.role.id &&
            a.id !== assignment.id
          );

          if (isDuplicate) {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('layout.configurations.users.messages.duplicateRole.title'),
              detail: this.translate.instant('layout.configurations.users.messages.duplicateRole.detail', { name: assignment.user.name, role: result.data.role.name })
            });
            return;
          }

          this.userRoleAssignments[existingIndex] = {
            ...this.userRoleAssignments[existingIndex],
            role: result.data.role,
            assignedDate: new Date(),
            assignedBy: 'Current User'
          };

          this.generateGroupedUserRoles();

          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.toast.success'),
            detail: this.translate.instant('layout.configurations.users.messages.roleAssignmentUpdated', { name: assignment.user.name })
          });
        }
      }
    });
  }

  editUserRoles(user: User): void {
    const currentRoles = this.userRoleAssignments
      .filter(assignment => assignment.user.id === user.id)
      .map(assignment => assignment.role);

    //  Filter to only active users
    const activeUsers = this.users.filter(u => !u.archived);

    const ref = this.dialogService.open(RoleAssignmentDialogComponent, {
      header: this.translate.instant('layout.configurations.users.assignRolesToUser.editRolesFor', {
        name: user.name
      }),
      width: '750px',
      modal: true,
      closable: true,
      maximizable: false,
      data: {
        users: activeUsers,  //  Only active users
        roles: this.roles,
        isEditMode: true,
        preSelectedUser: user,
        existingUserRole: {
          id: user.id,
          user: user,
          roles: currentRoles,
        },
      },
    });

    ref.onClose.subscribe((result) => {
      if (result && result.success) {
        this.loadUsersWithRolesFromApi();
      }
    });
  }

  deleteUserRoles(user: User): void {
    const userAssignments = this.userRoleAssignments.filter(assignment => assignment.user.id === user.id);

    if (userAssignments.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: this.translate.instant('layout.configurations.users.messages.noRoles'),
        detail: this.translate.instant('layout.configurations.users.messages.noRolesAssigned', { name: user.name })
      });
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('layout.configurations.users.assignRolesToUser.confirmRemoveAllRoles', {
        name: user.name,
      }),
      header: this.translate.instant('layout.configurations.users.assignRolesToUser.confirmDeletion'),
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translate.instant('layout.configurations.users.assignRolesToUser.deleteUserRole.delete'),
      rejectLabel: this.translate.instant('layout.configurations.users.assignRolesToUser.deleteUserRole.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.userRolesService.removeAllUserRoles(user.id, 'UI').subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant(
                'layout.configurations.users.assignRolesToUser.deleteUserRole.title'
              ),
              detail: this.translate.instant(
                'layout.configurations.users.assignRolesToUser.deleteUserRole.message',
                { name: user.name }
              )
            });
            this.userRoleAssignments = this.userRoleAssignments.filter(
              assignment => assignment.user.id !== user.id
            );
            this.uniqueUsers = this.uniqueUsers.filter(
              item => item.id !== user.id
            );
            this.filteredUserRoles = this.filteredUserRoles.filter(
              item => item.id !== user.id
            );
            this.totalRecords = this.filteredUserRoles.length;
            this.generateGroupedUserRoles();
            this.cdr.detectChanges();
          },
          error: (err: HttpErrorResponse) => {
            console.error('Delete error:', err);
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.toast.error'),
              detail: this.translate.instant('layout.configurations.users.messages.removeRolesFailed')
            });
          },
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('layout.configurations.users.assignRolesToUser.deleteUserRole.cancelDeletion.title'),
          detail: this.translate.instant('layout.configurations.users.assignRolesToUser.deleteUserRole.cancelDeletion.message'),
          life: 3000,
        });
      },
    });
  }

  resetUserPassword(): void {
    const user = this.userToReset!!;
    this.resetPasswordPanel.hide();
    this.confirmationService.confirm({
      message: this.translate.instant('layout.configurations.users.assignRolesToUser.confirmResetPassword', {
        name: user.name,
      }),
      header: this.translate.instant('layout.configurations.users.assignRolesToUser.confirmDeletion'),
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translate.instant('layout.configurations.users.assignRolesToUser.resetUserPassword.delete'),
      rejectLabel: this.translate.instant('layout.configurations.users.assignRolesToUser.resetUserPassword.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.authService.resetPassword(user.userId!!).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('layout.configurations.users.assignRolesToUser.resetUserPassword.title'),
              detail: this.translate.instant(
                'layout.configurations.users.assignRolesToUser.resetUserPassword.message',
                {
                  name: user.name,
                },
              )
            });
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.add({ severity: 'error', summary: this.translate.instant('common.toast.error'), detail: this.translate.instant('layout.configurations.users.messages.resetPasswordFailed') });
          },
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('layout.configurations.users.assignRolesToUser.resetUserPassword.cancelReset.title'),
          detail: this.translate.instant('layout.configurations.users.assignRolesToUser.resetUserPassword.cancelReset.message'),
          life: 3000,
        });
      },
    });
  }

  toggleResetPasswordPanel(event: Event, user: User) {
    this.resetPasswordPanel.toggle(event);
    this.userToReset = user;
  }

  resetMFA(): void {
    const user = this.userToReset!!;
    this.resetPasswordPanel.hide();
    this.confirmationService.confirm({
      message: this.translate.instant('layout.configurations.users.assignRolesToUser.confirmResetMFA', {
        name: user.name,
      }),
      header: this.translate.instant('layout.configurations.users.assignRolesToUser.confirmDeletion'),
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: this.translate.instant('layout.configurations.users.assignRolesToUser.resetUserMFA.delete'),
      rejectLabel: this.translate.instant('layout.configurations.users.assignRolesToUser.resetUserMFA.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.authService.resetMFA(user?.userId!!).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: this.translate.instant('layout.configurations.users.assignRolesToUser.resetUserMFA.title'),
              detail: this.translate.instant(
                'layout.configurations.users.assignRolesToUser.resetUserMFA.message',
                {
                  name: user.name,
                },
              )
            });
          },
          error: (err: HttpErrorResponse) => {
            this.messageService.add({ severity: 'error', summary: this.translate.instant('common.toast.error'), detail: this.translate.instant('layout.configurations.users.messages.resetMfaFailed') });
          },
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'info',
          summary: this.translate.instant('layout.configurations.users.assignRolesToUser.resetUserMFA.cancelReset.title'),
          detail: this.translate.instant('layout.configurations.users.assignRolesToUser.resetUserMFA.cancelReset.message'),
          life: 3000,
        });
      },
    });
  }

  private getNextAssignmentId(): number {
    return this.userRoleAssignments.length > 0
      ? Math.max(...this.userRoleAssignments.map(a => a.id)) + 1
      : 1;
  }

  removeUserRole(userRole: UserRole): void {
    if (!userRole || !userRole.id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.toast.error'),
        detail: this.translate.instant('layout.configurations.users.messages.invalidUserRoleData')
      });
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('layout.configurations.users.messages.confirmRemoveUserRole'),
      header: this.translate.instant('layout.configurations.users.messages.confirm'),
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        try {
          const initialLength = this.userRoles.length;
          this.userRoles = this.userRoles.filter(ur => ur.id !== userRole.id);

          if (this.userRoles.length === initialLength) {
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant('common.warning.title'),
              detail: this.translate.instant('layout.configurations.users.messages.userRoleNotFoundOrRemoved')
            });
            return;
          }


          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.toast.success'),
            detail: this.translate.instant('layout.configurations.users.messages.userRoleRemoved')
          });
        } catch (error) {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.toast.error'),
            detail: this.translate.instant('layout.configurations.users.messages.removeUserRoleFailed')
          });
        }
      }
    });
  }

  editUserRole(userRole: UserRole): void {
    if (!userRole || !userRole.id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.toast.error'),
        detail: this.translate.instant('layout.configurations.users.messages.invalidUserRoleData')
      });
      return;
    }

    try {
      const ref = this.dialogService.open(RoleAssignmentDialogComponent, {
        header: this.translate.instant('layout.configurations.users.messages.editUserRoles'),
        width: '750px',
        modal: true,
        closable: true,
        maximizable: false,
        data: {
          users: this.users,
          roles: this.roles,
          isEditMode: true,
          existingUserRole: userRole
        }
      });

      ref.onClose.subscribe((result) => {
        try {
          if (result && result.action === 'edit') {
            const index = this.userRoles.findIndex(ur => ur.id === userRole.id);
            if (index !== -1) {
              if (!result.data.roles || !Array.isArray(result.data.roles)) {
                this.messageService.add({
                  severity: 'error',
                  summary: this.translate.instant('common.toast.error'),
                  detail: this.translate.instant('layout.configurations.users.messages.invalidRoleData')
                });
                return;
              }

              this.userRoles[index] = {
                ...this.userRoles[index],
                roles: result.data.roles,
                assignedDate: new Date(),
                assignedBy: 'Current User'
              };


              this.messageService.add({
                severity: 'success',
                summary: this.translate.instant('common.toast.success'),
                detail: this.translate.instant('layout.configurations.users.messages.userRolesUpdated')
              });
            } else {
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant('common.toast.error'),
                detail: this.translate.instant('layout.configurations.users.messages.userRoleNotFoundForUpdate')
              });
            }
          }
        } catch (error) {
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('common.toast.error'),
            detail: this.translate.instant('layout.configurations.users.messages.updateUserRolesFailed')
          });
        }
      });
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.toast.error'),
        detail: this.translate.instant('layout.configurations.users.messages.openEditDialogFailed')
      });
    }
  }

  onSubmit(): void {
    if (this.userRoleForm.valid) {
      const formValue = this.userRoleForm.value;
      const selectedUser = formValue.selectedUser;
      const selectedRoles = formValue.selectedRoles;

      if (this.editingUserRole) {
        const existingUserRoleIndex = this.userRoles.findIndex(ur => ur.id === this.editingUserRole!.id);
        if (existingUserRoleIndex !== -1) {
          this.userRoles[existingUserRoleIndex].user = selectedUser;
          this.userRoles[existingUserRoleIndex].roles = [...selectedRoles];
          this.userRoles[existingUserRoleIndex].assignedDate = new Date();
          this.userRoles[existingUserRoleIndex].assignedBy = 'Current User';


          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.toast.success'),
            detail: this.translate.instant('layout.configurations.users.messages.rolesUpdatedFor', { name: selectedUser.name })
          });
        }
        this.editingUserRole = null;
      } else {
        const existingUserRoleIndex = this.userRoles.findIndex(ur => ur.user.id === selectedUser.id);

        if (existingUserRoleIndex !== -1) {
          this.userRoles[existingUserRoleIndex].roles = [...selectedRoles];
          this.userRoles[existingUserRoleIndex].assignedDate = new Date();
          this.userRoles[existingUserRoleIndex].assignedBy = 'Current User';


          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.toast.success'),
            detail: this.translate.instant('layout.configurations.users.messages.rolesUpdatedFor', { name: selectedUser.name })
          });
        } else {
          const newUserRole: UserRole = {
            id: this.userRoles.length + 1,
            user: selectedUser,
            roles: [...selectedRoles],
            assignedDate: new Date(),
            assignedBy: 'Current User'
          };
          this.userRoles.push(newUserRole);


          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('common.toast.success'),
            detail: this.translate.instant('layout.configurations.users.messages.rolesAssignedTo', { name: selectedUser.name })
          });
        }
      }

      this.userRoleForm.reset();
    } else {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.toast.error'),
        detail: this.translate.instant('layout.configurations.users.assignRolesToUser.roles.fillRequired')
      });
    }
  }

  assignRoles(): void {
    //  Filter to only active, non-archived users
    const activeUsers = this.users.filter(user => !user.archived);

    const ref = this.dialogService.open(RoleAssignmentDialogComponent, {
      header: this.translate.instant('layout.configurations.users.assignRolesToUser.title'),
      width: '750px',
      modal: true,
      closable: true,
      maximizable: false,
      data: {
        users: activeUsers,  //  Only active users
        roles: this.roles,
        isEditMode: false,
      },
    });

    ref.onClose.subscribe((result) => {
      if (result && result.action === 'create' && result.data.user && result.data.roles) {
        const newAssignments: UserRoleAssignment[] = result.data.roles.map((role: Role) => ({
          id: this.getNextAssignmentId(),
          user: result.data.user,
          role: role,
          assignedDate: new Date(),
          assignedBy: 'Current User'
        }));

        this.userRoleAssignments.push(...newAssignments);
        this.generateGroupedUserRoles();
        this.loadUsersWithRolesFromApi();
      }
    });
  }

  cancelEdit(): void {
    this.editingUserRole = null;
    this.userRoleForm.reset();
    this.messageService.add({
      severity: 'info',
      summary: this.translate.instant('layout.configurations.users.messages.editCancelled.title'),
      detail: this.translate.instant('layout.configurations.users.messages.editCancelled.detail')
    });
  }

  isEditing(): boolean {
    return this.editingUserRole !== null;
  }

  /**
   * Updates user permissions based on their assigned roles
   * @param userId The user ID to update permissions for
   */
  private updateUserPermissions(userId: number): void {
    const userRoleAssignments = this.userRoles.filter(ur => ur.user.id === userId);

    const userRolesForBridge = userRoleAssignments.flatMap(ur =>
      ur.roles.map(role => ({
        id: role.id || 0,
        name: role.name,
        description: role.description || ''
      }))
    );

    const rolePermissions = this.roles.map(role => ({
      role: role.name,
      dashboard_global: true,
      dashboard_client: true,
      dashboard_leader: true,
      dashboard_language: true,
      dashboard_promotion: true,
      dashboard_medical_visit: true,
      cards_view_read: true,
      cards_view_write: role.name === 'Super_Admin',
      career_details: true,
      card_view_validate_role: role.name === 'Super_Admin',
      own_page: true,
      client_read: role.name !== 'Employee',
      client_write: role.name === 'Super_Admin',
      project_read: role.name !== 'Employee',
      project_write: role.name === 'Super_Admin',
      staffing_read: role.name !== 'Employee',
      staffing_write: role.name === 'Super_Admin',
      promotion_read: role.name !== 'Employee',
      promotion_identify_candidate: role.name === 'Super_Admin',
      promotion_confirm_identification: role.name === 'Super_Admin',
      promotion_schedule_interview: role.name === 'Super_Admin',
      promotion_manage_candidate: role.name === 'Super_Admin',
      promotion_commit: role.name === 'Super_Admin',
      evaluation_read: role.name !== 'Employee',
      evaluation_write: role.name === 'Super_Admin',
      pool_read: role.name !== 'Employee',
      pool_write: role.name === 'Super_Admin',
      manager_read: ['Super Admin', 'CEO', 'Manager', 'Director'].includes(role.name),
      manager_write: ['Super Admin', 'CEO', 'Manager', 'Director'].includes(role.name),
      manager_people: 'Manager' === role.name,
      bsa: 'BSA' === role.name,
      production: role.name !== 'Employee',
      people_read: ['Super Admin', 'CEO', 'Manager', 'Director'].includes(role.name),
      people_write: ['Super Admin', 'CEO', 'Manager', 'Director'].includes(role.name),
      notification_read: true,
      salaries_read: role.name !== 'Employee',
      salaries_write: role.name === 'Super_Admin',
      salary_validate: false,
      config_staffing: role.name !== 'Employee',
      config_bsa: role.name !== 'Employee',
      config_invoicing: role.name !== 'Employee',
      config_performance: role.name !== 'Employee',
      config_user_management: role.name === 'Super_Admin',
      invoicing_read: role.name === 'Super_Admin',
      invoicing_service: role.name === 'Super_Admin',
      invoicing_admin: role.name === 'Super_Admin',
      bsa_org_read: role.name === 'Super_Admin',
      bsa_org_write: role.name === 'Super_Admin',
      audit_log_read: role.name === 'Super_Admin',
      opportunity_read: role.name !== 'Employee',
      opportunity_write: role.name === 'Super_Admin',
      demand_read: role.name !== 'Employee',
      demand_write: role.name === 'Super_Admin',
      forecast_read: role.name !== 'Employee',
      forecast_write: role.name === 'Super_Admin',
      people_at_risk_read: role.name !== 'Employee',
      people_at_risk_write: role.name === 'Super_Admin',
      billing_read: role.name === 'Super_Admin',
      billing_write: role.name === 'Super_Admin',
      feedback_assigned_handler: role.name === 'Super_Admin',
      feedback_assignment_manager: role.name === 'Super_Admin'
    } as any));

    this.rolePermissionBridge.updateUserPermissions(userRolesForBridge, rolePermissions);

  }

  /**
   * Gets current user permissions for display
   * @param userId The user ID
   * @returns Array of permission names
   */
  getUserPermissions(userId: number): string[] {
    const userRoleAssignments = this.userRoles.filter(ur => ur.user.id === userId);
    const userRolesForBridge = userRoleAssignments.flatMap(ur =>
      ur.roles.map(role => ({
        id: role.id || 0,
        name: role.name,
        description: role.description || ''
      }))
    );

    const rolePermissions = this.roles.map(role => ({
      role: role.name,
      dashboard_global: true,
      dashboard_client: true,
      dashboard_leader: true,
      dashboard_language: true,
      dashboard_promotion: true,
      dashboard_medical_visit: true,
      cards_view_read: true,
      cards_view_write: role.name === 'Super_Admin',
      career_details: true,
      card_view_validate_role: role.name === 'Super_Admin',
      own_page: true,
      client_read: role.name !== 'Employee',
      client_write: role.name === 'Super_Admin',
      project_read: role.name !== 'Employee',
      project_write: role.name === 'Super_Admin',
      staffing_read: role.name !== 'Employee',
      staffing_write: role.name === 'Super_Admin',
      promotion_read: role.name !== 'Employee',
      promotion_identify_candidate: role.name === 'Super_Admin',
      promotion_confirm_identification: role.name === 'Super_Admin',
      promotion_schedule_interview: role.name === 'Super_Admin',
      promotion_manage_candidate: role.name === 'Super_Admin',
      promotion_commit: role.name === 'Super_Admin',
      evaluation_read: role.name !== 'Employee',
      evaluation_write: role.name === 'Super_Admin',
      pool_read: role.name !== 'Employee',
      pool_write: role.name === 'Super_Admin',
      manager_read: ['Super Admin', 'CEO', 'Manager', 'Director'].includes(role.name),
      manager_write: ['Super Admin', 'CEO', 'Manager', 'Director'].includes(role.name),
      manager_people: 'Manager' === role.name,
      bsa: 'BSA' === role.name,
      production: role.name !== 'Employee',
      people_read: ['Super Admin', 'CEO', 'Manager', 'Director'].includes(role.name),
      people_write: ['Super Admin', 'CEO', 'Manager', 'Director'].includes(role.name),
      notification_read: true,
      salaries_read: role.name !== 'Employee',
      salaries_write: role.name === 'Super_Admin',
      salary_validate: false,
      config_staffing: role.name !== 'Employee',
      config_bsa: role.name !== 'Employee',
      config_invoicing: role.name !== 'Employee',
      config_performance: role.name !== 'Employee',
      config_user_management: role.name === 'Super_Admin',
      invoicing_read: role.name === 'Super_Admin',
      invoicing_service: role.name === 'Super_Admin',
      invoicing_admin: role.name === 'Super_Admin',
      bsa_org_read: role.name === 'Super_Admin',
      bsa_org_write: role.name === 'Super_Admin',
      audit_log_read: role.name === 'Super_Admin',
      opportunity_read: role.name !== 'Employee',
      opportunity_write: role.name === 'Super_Admin',
      demand_read: role.name !== 'Employee',
      demand_write: role.name === 'Super_Admin',
      forecast_read: role.name !== 'Employee',
      forecast_write: role.name === 'Super_Admin',
      people_at_risk_read: role.name !== 'Employee',
      people_at_risk_write: role.name === 'Super_Admin',
      billing_read: role.name === 'Super_Admin',
      billing_write: role.name === 'Super_Admin',
      feedback_assigned_handler: role.name === 'Super_Admin',
      feedback_assignment_manager: role.name === 'Super_Admin'
    } as any));

    const permissions = this.rolePermissionBridge.convertUserRolesToPermissions(userRolesForBridge, rolePermissions);

    const userRole = userRolesForBridge[0];
    if (userRole) {
      const rolePermission = rolePermissions.find(rp => rp.role === userRole.name);
      if (rolePermission) {
        return this.rolePermissionBridge.getEnabledPermissionNames(rolePermission);
      }
    }

    return [];
  }

  /**
   * Gets the role name by role ID
   * @param roleId The role ID
   * @returns The role name
   */
  private getRoleName(roleId: number): string {
    const role = this.roles.find(r => r.id === roleId);
    return role ? role.name : 'Unknown';
  }

  private getBackendUserId(user: User): number | null {
    return user?.userId ?? null;
  }
}
